import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Checkbox,
  Button,
  Space,
  Notification,
} from '@arco-design/web-react';
import { FormInstance } from '@arco-design/web-react/es/Form';
import { IconLock, IconUser } from '@arco-design/web-react/icon';
import { useRef } from 'react';
import useStorage from '@/utils/useStorage';
import useLocale from '@/utils/useLocale';
import locale from './locale';
import request from '@/request/request';

export default function LoginForm() {
  const formRef = useRef<FormInstance>();
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginParams, setLoginParams, removeLoginParams] =
    useStorage('loginParams');
  const t = useLocale(locale);
  const [rememberPassword, setRememberPassword] = useState(!!loginParams);

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  function afterLoginSuccess(params, accessToken) {
    if (rememberPassword) {
      setLoginParams(JSON.stringify(params));
    } else {
      removeLoginParams();
    }
    localStorage.setItem('token', accessToken);

    Notification.success({
      title: '登录成功',
      content: '正在进入系统...',
      duration: 1e3,
    });

    setTimeout(() => {
      window.location.href = '/';
    }, 1e3);
  }

  function login(params) {
    setErrorMessage('');
    setLoading(true);
    request('/archivesService/agency/login', {
      method: 'POST',
      data: params,
    })
      .then((resp) => {
        if (resp.code === 200) {
          afterLoginSuccess(params, resp.data.token);
        } else {
          setErrorMessage(resp.message || t['login.form.login.errMsg']);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function onSubmitClick() {
    formRef.current.validate().then((values) => {
      login(values);
    });
  }

  useEffect(() => {
    const rememberPassword = !!loginParams;
    setRememberPassword(rememberPassword);
    if (formRef.current && rememberPassword) {
      const parseParams = JSON.parse(loginParams);
      formRef.current.setFieldsValue(parseParams);
    }
  }, [loginParams]);

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        background:
          'linear-gradient(135deg, #0A0A1A 0%, #1A1A3A 50%, #2A2A5A 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '1000px',
      }}
    >
      {/* 深色渐变背景 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `
          radial-gradient(circle at 20% 20%, rgba(26, 115, 232, 0.1) 0%, transparent 40%),
          radial-gradient(circle at 80% 80%, rgba(52, 168, 83, 0.1) 0%, transparent 40%)
        `,
          zIndex: 1,
        }}
      />

      {/* 精细网格 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
        `,
          backgroundSize: '30px 30px',
          animation: 'gridMove 40s linear infinite',
          zIndex: 2,
        }}
      />

      {/* 柔和光晕 */}
      <div
        style={{
          position: 'absolute',
          left: mousePos.x - 100,
          top: mousePos.y - 100,
          width: 200,
          height: 200,
          background:
            'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(30px)',
          transition: 'left 0.2s ease-out, top 0.2s ease-out',
          pointerEvents: 'none',
          zIndex: 3,
        }}
      />

      {/* 主登录卡片 */}
      <div
        style={{
          position: 'relative',
          zIndex: 4,
          width: 480,
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: `
  0 20px 40px rgba(0, 0, 0, 0.2),
  inset 0 1px 0 rgba(255, 255, 255, 0.1)
`,
          borderRadius: '24px',
          padding: '50px 40px',

          transformStyle: 'preserve-3d',
          transform: 'rotateX(var(--rotateX)) rotateY(var(--rotateY))',
          transition: 'transform 0.3s',
          willChange: 'transform',
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const rotateY = ((x - centerX) / centerX) * 3;
          const rotateX = ((centerY - y) / centerY) * 3;
          e.currentTarget.style.setProperty('--rotateX', `${rotateX}deg`);
          e.currentTarget.style.setProperty('--rotateY', `${rotateY}deg`);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.setProperty('--rotateX', '0deg');
          e.currentTarget.style.setProperty('--rotateY', '0deg');
        }}
      >
        {/* 顶部装饰条 */}
        {/* <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #1A73E8, #34A853, #FBBC04)',
            borderRadius: '24px 24px 0 0',
            zIndex: 5,
          }}
        /> */}

        {/* 左上角装饰 */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: 20,
            width: 40,
            height: 40,
            border: '2px solid rgba(26, 115, 232, 0.3)',
            borderRight: 'none',
            borderBottom: 'none',
            borderRadius: '8px 0 0 0',
          }}
        />

        {/* 右上角装饰 */}
        <div
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 40,
            height: 40,
            border: '2px solid rgba(52, 168, 83, 0.3)',
            borderLeft: 'none',
            borderBottom: 'none',
            borderRadius: '0 8px 0 0',
          }}
        />

        {/* 主内容区域 */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
          }}
        >
          {/* Logo和标题 */}
          <div
            style={{
              textAlign: 'center',
              marginBottom: '40px',
              position: 'relative',
            }}
          >
            {/* <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                marginBottom: '20px',
                border: '1px solid rgba(26, 115, 232, 0.2)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(135deg, transparent 40%, rgba(255, 255, 255, 0.1) 50%, transparent 60%)',
                  animation: 'shine 3s ease-in-out infinite',
                }}
              />
            </div> */}

            <h1
              style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#FFFFFF',
                margin: '0 0 8px 0',
                letterSpacing: '1px',
                background: 'linear-gradient(90deg, #1A73E8, #34A853)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              健康数据大屏
            </h1>

            <p
              style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px',
                margin: 0,
                letterSpacing: '2px',
                fontWeight: 300,
              }}
            >
              {/* 健康数据大屏的英文 */}
              Health Data Dashboard
            </p>
          </div>

          {/* 错误提示 */}
          {errorMessage && (
            <div
              style={{
                background: 'rgba(234, 67, 53, 0.1)',
                borderLeft: '4px solid #EA4335',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
                color: '#FF6B6B',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backdropFilter: 'blur(10px)',
                animation: 'fadeInUp 0.3s ease-out',
              }}
            >
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  background: '#EA4335',
                  borderRadius: '50%',
                  flexShrink: 0,
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              {errorMessage}
            </div>
          )}

          {/* 表单 */}
          <Form
            ref={formRef}
            layout="vertical"
            initialValues={{ account: '', password: '' }}
            style={{ width: '100%' }}
          >
            {/* 账号 */}
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  display: 'block',
                  paddingLeft: '2px',
                }}
              >
                账号
              </label>
              <div
                style={{
                  position: 'relative',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.border =
                    '1px solid rgba(26, 115, 232, 0.5)';
                  e.currentTarget.style.boxShadow =
                    '0 0 0 2px rgba(26, 115, 232, 0.1)';
                  e.currentTarget.style.background = 'rgba(26, 115, 232, 0.05)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.border =
                    '1px solid rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.background =
                    'rgba(255, 255, 255, 0.03)';
                }}
              >
                <Form.Item field={'account'} noStyle>
                  <Input
                    prefix={
                      <IconUser
                        style={{
                          fontSize: 18,
                          margin: '0 12px',
                        }}
                      />
                    }
                    placeholder="请输入账号"
                    onPressEnter={onSubmitClick}
                    style={{
                      height: '52px',
                      background: 'transparent',
                      border: 'none',
                      fontSize: '15px',
                      color: '#FFFFFF',
                      padding: '0 20px 0 0',
                      width: '100%',
                    }}
                  />
                </Form.Item>
              </div>
            </div>

            {/* 密码 */}
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  display: 'block',
                  paddingLeft: '2px',
                }}
              >
                密码
              </label>
              <div
                style={{
                  position: 'relative',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s',
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.border =
                    '1px solid rgba(52, 168, 83, 0.5)';
                  e.currentTarget.style.boxShadow =
                    '0 0 0 2px rgba(52, 168, 83, 0.1)';
                  e.currentTarget.style.background = 'rgba(52, 168, 83, 0.05)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.border =
                    '1px solid rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.background =
                    'rgba(255, 255, 255, 0.03)';
                }}
              >
                <Form.Item field={'password'} noStyle>
                  <Input.Password
                    prefix={
                      <IconLock
                        style={{
                          fontSize: 18,
                          margin: '0 12px',
                        }}
                      />
                    }
                    placeholder="请输入密码"
                    onPressEnter={onSubmitClick}
                    style={{
                      height: '52px',
                      background: 'transparent',
                      border: 'none',
                      fontSize: '15px',
                      color: '#FFFFFF',
                      padding: '0 20px 0 0',
                      width: '100%',
                    }}
                    visibilityToggle
                  />
                </Form.Item>
              </div>
            </div>

            {/* 登录按钮 */}
            <Button
              type="primary"
              long
              onClick={onSubmitClick}
              loading={loading}
              style={{
                height: '52px',
                background: 'linear-gradient(135deg, #1A73E8, #34A853)',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'all 0.3s',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(26, 115, 232, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '30px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 20px rgba(26, 115, 232, 0.3)';
                e.currentTarget.style.background =
                  'linear-gradient(135deg, #34A853, #1A73E8)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow =
                  '0 4px 12px rgba(26, 115, 232, 0.2)';
                e.currentTarget.style.background =
                  'linear-gradient(135deg, #1A73E8, #34A853)';
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow =
                  '0 2px 8px rgba(26, 115, 232, 0.2)';
              }}
            >
              {loading ? (
                <span>登录中...</span>
              ) : (
                <>
                  <span>登录系统</span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                      transition: 'transform 0.3s',
                    }}
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </>
              )}
              {/* 按钮高光 */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
                  transition: 'left 0.6s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.left = '100%';
                }}
              />
            </Button>
          </Form>

          {/* 底部版权信息 */}
          {/* <div
            style={{
              textAlign: 'center',
              marginTop: '30px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '11px',
              letterSpacing: '0.5px',
              lineHeight: 1.6,
            }}
          >
            <p style={{ margin: 0 }}>© 2024 Archive Management System</p>
            <p style={{ margin: '4px 0 0 0' }}>Powered by hhmdtech.com</p>
          </div> */}
        </div>
      </div>

      {/* 全局动画样式 */}
      <style>
        {`
          @keyframes gridMove {
            0% { transform: translateY(0); }
            100% { transform: translateY(30px); }
          }
          
          @keyframes shine {
            0% { transform: translateX(-100%) rotate(45deg); }
            100% { transform: translateX(200%) rotate(45deg); }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          /* 输入框占位符样式 */
          .arco-input::placeholder {
            color: rgba(255, 255, 255, 0.3) !important;
            font-weight: 300;
          }
          
          .arco-input:-ms-input-placeholder {
            color: rgba(255, 255, 255, 0.3) !important;
            font-weight: 300;
          }
          
          .arco-input::-ms-input-placeholder {
            color: rgba(255, 255, 255, 0.3) !important;
            font-weight: 300;
          }
          
          /* 密码可见性图标样式 */
          .arco-input-password-suffix {
            color: rgba(255, 255, 255, 0.4) !important;
            padding-right: 12px;
            transition: color 0.2s;
          }
          
          .arco-input-password-suffix:hover {
            color: rgba(255, 255, 255, 0.8) !important;
          }
          
          /* 按钮加载状态 */
          .arco-btn-loading {
            opacity: 0.9;
          }
          
          /* 移除输入框聚焦轮廓 */
          .arco-input:focus {
            outline: none !important;
            box-shadow: none !important;
          }
          
          /* 滚动条美化 */
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          
          ::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
        `}
      </style>
    </div>
  );
}
